import React, { useState } from "react";
import BreadCrumb from "Common/BreadCrumb";
import { useQuery } from "react-query";
import { Send, CheckCircle2, MailOpen, MousePointerClick, XCircle } from "lucide-react";
import { getEmailLogs, getEmailLogStats } from "services/emailLog.service";
import { EmailLogFilters, EmailStatus } from "types/emailLog.type";

const STATUS_OPTIONS: { value: EmailStatus | ""; label: string }[] = [
  { value: "", label: "Any" },
  { value: "sent", label: "Sent" },
  { value: "delivered", label: "Delivered" },
  { value: "opened", label: "Opened" },
  { value: "clicked", label: "Clicked" },
  { value: "soft_bounce", label: "Soft bounce" },
  { value: "hard_bounce", label: "Hard bounce" },
  { value: "blocked", label: "Blocked" },
  { value: "spam", label: "Spam" },
  { value: "unsubscribed", label: "Unsubscribed" },
  { value: "error", label: "Error" },
];

const FAILED_STATUSES: EmailStatus[] = ["hard_bounce", "soft_bounce", "blocked", "error"];

function StatCard({
  label,
  value,
  icon: Icon,
  tone,
}: {
  label: string;
  value: React.ReactNode;
  icon: React.ElementType;
  tone: string;
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} className={tone} />
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</p>
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
    </div>
  );
}

const statusBadge = (status: EmailStatus) => {
  const failed = FAILED_STATUSES.includes(status);
  const tone = failed
    ? "bg-red-50 text-red-700"
    : status === "opened" || status === "clicked"
    ? "bg-blue-50 text-blue-700"
    : status === "delivered"
    ? "bg-green-50 text-green-700"
    : "bg-gray-100 text-gray-600";
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-semibold uppercase tracking-wide ${tone}`}>
      {status.replace(/_/g, " ")}
    </span>
  );
};

const EmailLogs = () => {
  const [filters, setFilters] = useState<EmailLogFilters>({});
  const [draft, setDraft] = useState<EmailLogFilters>({});
  const [page, setPage] = useState(1);
  const limit = 20;

  const { data: statsRes } = useQuery(["email-log-stats", filters], () =>
    getEmailLogStats(filters)
  );
  const { data: logsRes, isLoading } = useQuery(
    ["email-logs", filters, page],
    () => getEmailLogs({ page, limit }, filters)
  );

  const stats = statsRes?.data;
  const logs = logsRes?.data?.data ?? [];
  const total = logsRes?.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  const applyFilters = () => {
    setPage(1);
    setFilters(draft);
  };

  return (
    <React.Fragment>
      <BreadCrumb title="Email Logs" pageTitle="Email Logs" />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-5">
        <StatCard label="Sent" value={stats?.sent ?? "—"} icon={Send} tone="text-gray-500" />
        <StatCard label="Delivered" value={stats?.delivered ?? "—"} icon={CheckCircle2} tone="text-green-600" />
        <StatCard
          label={`Opened${stats ? ` (${stats.openRate}%)` : ""}`}
          value={stats?.opened ?? "—"}
          icon={MailOpen}
          tone="text-blue-600"
        />
        <StatCard label="Clicked" value={stats?.clicked ?? "—"} icon={MousePointerClick} tone="text-indigo-600" />
        <StatCard label="Failed / Bounced" value={stats?.failed ?? "—"} icon={XCircle} tone="text-red-600" />
      </div>

      {!!stats?.topCategories?.length && (
        <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm mb-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Top Categories (current filter)
          </p>
          <div className="flex flex-wrap gap-2">
            {stats.topCategories.map((c) => (
              <span
                key={c.category}
                className="px-3 py-1.5 rounded-full border border-gray-200 text-xs text-gray-700"
              >
                {c.category} <span className="text-gray-400">· {c.count}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm mb-5">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Category</label>
            <input
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              placeholder="otp / kyc_abandoned / user_account"
              value={draft.category ?? ""}
              onChange={(e) => setDraft({ ...draft, category: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Status</label>
            <select
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              value={draft.status ?? ""}
              onChange={(e) => setDraft({ ...draft, status: e.target.value as EmailStatus | "" })}
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Recipient</label>
            <input
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              placeholder="user@example.com"
              value={draft.recipient ?? ""}
              onChange={(e) => setDraft({ ...draft, recipient: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">From</label>
            <input
              type="date"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              value={draft.from ?? ""}
              onChange={(e) => setDraft({ ...draft, from: e.target.value })}
            />
          </div>
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="block text-xs text-gray-500 mb-1">To</label>
              <input
                type="date"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                value={draft.to ?? ""}
                onChange={(e) => setDraft({ ...draft, to: e.target.value })}
              />
            </div>
            <button
              onClick={applyFilters}
              className="self-end bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg px-4 py-2"
            >
              Filter
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden shadow-sm">
        {isLoading ? (
          <div className="py-12 text-center text-sm text-gray-400">Loading email logs…</div>
        ) : logs.length === 0 ? (
          <div className="py-16 text-center text-sm text-gray-500">No email logs match this filter</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Sent</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Category</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Recipient</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Subject</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Opens</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Clicks</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">User</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {logs.map((log) => (
                    <tr key={log._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                        {new Date(log.createdAt).toLocaleDateString()}, {new Date(log.createdAt).toLocaleTimeString()}
                      </td>
                      <td className="px-4 py-3">{statusBadge(log.status)}</td>
                      <td className="px-4 py-3 text-gray-600">{log.category}</td>
                      <td className="px-4 py-3 text-gray-900">{log.recipient}</td>
                      <td className="px-4 py-3 text-gray-600 max-w-xs truncate">{log.subject}</td>
                      <td className="px-4 py-3 text-gray-600">{log.opens}</td>
                      <td className="px-4 py-3 text-gray-600">{log.clicks}</td>
                      <td className="px-4 py-3 text-gray-600">
                        {log.user ? `${log.user.firstName ?? ""} ${log.user.lastName ?? ""}`.trim() || log.user.email : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
              <p className="text-xs text-gray-400">
                Page {page} of {totalPages} · {total} total
              </p>
              <div className="flex gap-2">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="text-sm text-indigo-600 hover:underline disabled:text-gray-300 disabled:no-underline"
                >
                  Previous
                </button>
                <button
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="text-sm text-indigo-600 hover:underline disabled:text-gray-300 disabled:no-underline"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </React.Fragment>
  );
};

export default EmailLogs;
